import React, { useMemo } from 'react';
import CareBadge from 'shared/components/Badges/CareBadge';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { ChannelViewChannelPetFragment, useCheckPetEnrollmentSubscription } from 'shared/types/graphql';
import { HStack } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import {
  getSubscriptionPaymentStatus,
  isEnrollmentConsideredActive,
} from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/utils/careEnrollments';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';

interface PatientItemProps {
  pet: ChannelViewChannelPetFragment;
  handlePetClick: (id: string) => void;
  refetchChannel: () => void;
}

export const PatientItem = ({ pet, handlePetClick, refetchChannel }: PatientItemProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();

  useCheckPetEnrollmentSubscription({
    variables: {
      where: {
        organizationPetId: pet.organizationPet?.id || '',
      },
    },
    skip: !pet.organizationPet?.id,
    onData: ({ data }) => {
      if (data.data?.checkPetEnrollment?.isEnrolled) {
        refetchChannel();
      }
    },
  });

  const isCareEnabled = useMemo(() => isFeatureEnabled(FeatureFlagName.CarePlans), [isFeatureEnabled]);
  const isCareMembershipActive = useMemo(() => {
    const enrollment = pet.organizationPet?.carePlanEnrollments?.find((enrollment) =>
      isEnrollmentConsideredActive(enrollment),
    );
    return isCareEnabled && !!enrollment;
  }, [isCareEnabled, pet.organizationPet?.carePlanEnrollments]);
  const careSubscriptionPaymentStatus = useMemo(
    () => getSubscriptionPaymentStatus(pet.organizationPet),
    [pet.organizationPet],
  );

  return (
    <HStack spacing={1} key={pet.id} ml={1}>
      <PetAvatar speciesType={pet?.speciesType} isDeceased={pet?.computedIsDeceased} iconOnly />
      <Text
        className="PetAvatar_Label" // used in invocing panel
        fontWeight="bold"
        cursor="pointer"
        onClick={(): void => {
          handlePetClick(pet?.id);
        }}
      >
        {pet?.name || ''}
      </Text>
      {isCareEnabled && (
        <CareBadge isCareMember={isCareMembershipActive} paymentStatus={careSubscriptionPaymentStatus} />
      )}
    </HStack>
  );
};
